import React, { FC, useState } from 'react'
import {
  Box,
  Typography,
  TableRow,
  TableCell,
  IconButton,
  Tooltip,
  Stack,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import {
  useTextReplacementsQuery,
  useCreateTextReplacementMutation,
  useUpdateTextReplacementMutation,
  useDeleteTextReplacementMutation,
} from '@/features/internal/text-replacements/hooks/useTextReplacementsQuery'
import type { TextReplacementRule } from '@/features/internal/text-replacements/apis/textReplacements'
import {
  Banner,
  DataTable,
  DialogShell,
  EmptyState,
  NotionButton,
  NotionTextField,
  PageLoading,
  PageScaffold,
} from '@/apps/latest/components/portal/notion'
import { PORTAL_TEXT } from '@/apps/latest/shared/styles/portalTypography'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { RADIUS } from '@/shared/styles/radii'

type DialogMode = 'create' | 'edit' | 'delete' | null

export const TextReplacementsPage: FC = () => {
  const { rules, isLoading, error } = useTextReplacementsQuery()
  const createMutation = useCreateTextReplacementMutation()
  const updateMutation = useUpdateTextReplacementMutation()
  const deleteMutation = useDeleteTextReplacementMutation()

  const [dialogMode, setDialogMode] = useState<DialogMode>(null)
  const [selectedRule, setSelectedRule] = useState<TextReplacementRule | null>(
    null
  )
  const [formData, setFormData] = useState({
    target_text: '',
    replacement_text: '',
    description: '',
  })
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleOpenCreateDialog = () => {
    setFormData({
      target_text: '',
      replacement_text: '',
      description: '',
    })
    setFormErrors({})
    setDialogMode('create')
  }

  const handleOpenEditDialog = (rule: TextReplacementRule) => {
    setSelectedRule(rule)
    setFormData({
      target_text: rule.target_text,
      replacement_text: rule.replacement_text,
      description: rule.description || '',
    })
    setFormErrors({})
    setDialogMode('edit')
  }

  const handleOpenDeleteDialog = (rule: TextReplacementRule) => {
    setSelectedRule(rule)
    setDialogMode('delete')
  }

  const handleCloseDialog = () => {
    if (!isSubmitting) {
      setDialogMode(null)
      setSelectedRule(null)
      setFormData({
        target_text: '',
        replacement_text: '',
        description: '',
      })
      setFormErrors({})
    }
  }

  const validateForm = () => {
    const errors: Record<string, string> = {}
    if (!formData.target_text.trim()) {
      errors.target_text = '置換対象文字列は必須です'
    } else if (formData.target_text.length > 200) {
      errors.target_text = '置換対象文字列は200文字以内で入力してください'
    }
    if (formData.replacement_text.length > 200) {
      errors.replacement_text = '置換後文字列は200文字以内で入力してください'
    }
    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateForm()) return
    setIsSubmitting(true)
    try {
      if (dialogMode === 'create') {
        await createMutation.mutateAsync({
          target_text: formData.target_text.trim(),
          replacement_text: formData.replacement_text,
          description: formData.description.trim() || undefined,
        })
      } else if (dialogMode === 'edit' && selectedRule) {
        await updateMutation.mutateAsync({
          id: selectedRule.id,
          data: {
            target_text: formData.target_text.trim(),
            replacement_text: formData.replacement_text,
            description: formData.description.trim() || undefined,
          },
        })
      }
      handleCloseDialog()
    } catch (_err) {
      // mutation 内で処理
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDelete = async () => {
    if (!selectedRule) return
    setIsSubmitting(true)
    try {
      await deleteMutation.mutateAsync(selectedRule.id)
      handleCloseDialog()
    } catch (_err) {
      // mutation 内で処理
    } finally {
      setIsSubmitting(false)
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  if (error && !rules.length) {
    return (
      <PageScaffold title="文字列置換設定">
        <Banner severity="danger">{error.message}</Banner>
      </PageScaffold>
    )
  }

  return (
    <PageScaffold
      title="文字列置換設定"
      meta={rules.length > 0 ? `全 ${rules.length} 件` : undefined}
      actions={
        <NotionButton
          variant="primary"
          startIcon={<AddIcon />}
          onClick={handleOpenCreateDialog}
        >
          新規ルール
        </NotionButton>
      }
    >
      <Typography sx={{ ...PORTAL_TEXT.caption, mb: 2 }}>
        SOAP
        生成時に自動的に適用される文字列置換ルールを管理します。新しいルールほど優先度が高くなります。
      </Typography>

      {isLoading && !rules.length ? (
        <PageLoading />
      ) : rules.length === 0 ? (
        <EmptyState
          icon={<SwapHorizIcon />}
          title="置換ルールがありません"
          hint="「新規ルール」 ボタンから作成してください"
        />
      ) : (
        <DataTable
          columns={[
            { key: 'rule', label: '置換ルール' },
            { key: 'description', label: '説明' },
            { key: 'created_at', label: '作成日時' },
            { key: 'actions', label: '操作', align: 'right' },
          ]}
        >
          {rules.map((rule) => (
            <TableRow key={rule.id}>
              <TableCell>
                <Stack direction="row" alignItems="center" gap={1}>
                  <Typography sx={PORTAL_TEXT.cellBody}>
                    {rule.target_text}
                  </Typography>
                  <ArrowForwardIcon
                    sx={{ fontSize: 14, color: 'text.disabled' }}
                  />
                  <Typography sx={PORTAL_TEXT.cellBody}>
                    {rule.replacement_text}
                  </Typography>
                </Stack>
              </TableCell>
              <TableCell>
                <Typography sx={PORTAL_TEXT.cellSecondary}>
                  {rule.description || '-'}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography sx={PORTAL_TEXT.cellMeta}>
                  {formatDate(rule.created_at)}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Stack direction="row" gap={0.5} justifyContent="flex-end">
                  <Tooltip title="編集">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenEditDialog(rule)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="削除">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDeleteDialog(rule)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </DataTable>
      )}

      <DialogShell
        open={dialogMode === 'create' || dialogMode === 'edit'}
        onClose={handleCloseDialog}
        title={dialogMode === 'create' ? '新規ルール追加' : 'ルール編集'}
        maxWidth="sm"
        disabled={isSubmitting}
        actions={
          <>
            <NotionButton
              variant="secondary"
              onClick={handleCloseDialog}
              disabled={isSubmitting}
            >
              キャンセル
            </NotionButton>
            <NotionButton
              variant="primary"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting
                ? '保存中…'
                : dialogMode === 'create'
                ? '追加'
                : '更新'}
            </NotionButton>
          </>
        }
      >
        <Stack gap={2}>
          <NotionTextField
            label="置換対象文字列"
            value={formData.target_text}
            onChange={(e) =>
              setFormData({ ...formData, target_text: e.target.value })
            }
            error={!!formErrors.target_text}
            helperText={
              formErrors.target_text ||
              `入力例: 座薬、ｍｇ（${formData.target_text.length}/200文字）`
            }
            required
            disabled={isSubmitting}
          />
          <NotionTextField
            label="置換後文字列"
            value={formData.replacement_text}
            onChange={(e) =>
              setFormData({
                ...formData,
                replacement_text: e.target.value,
              })
            }
            error={!!formErrors.replacement_text}
            helperText={
              formErrors.replacement_text ||
              `入力例: 坐薬、mg、空欄で削除（${formData.replacement_text.length}/200文字）`
            }
            disabled={isSubmitting}
          />
          <NotionTextField
            label="説明"
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
            helperText="任意: ルールの目的や理由を記載"
            multiline
            rows={2}
            disabled={isSubmitting}
          />
        </Stack>
      </DialogShell>

      <DialogShell
        open={dialogMode === 'delete'}
        onClose={handleCloseDialog}
        title="ルールを削除"
        maxWidth="xs"
        disabled={isSubmitting}
        actions={
          <>
            <NotionButton
              variant="secondary"
              onClick={handleCloseDialog}
              disabled={isSubmitting}
            >
              キャンセル
            </NotionButton>
            <NotionButton
              variant="destructive"
              onClick={handleDelete}
              disabled={isSubmitting}
            >
              {isSubmitting ? '削除中…' : '削除'}
            </NotionButton>
          </>
        }
      >
        <Stack gap={1.5}>
          <Typography sx={PORTAL_TEXT.rowLabel}>
            以下のルールを削除してもよろしいですか？
          </Typography>
          {selectedRule && (
            <Box
              sx={{
                bgcolor: colorTokens.overlay.soft,
                border: `1px solid ${colorTokens.border.subtle}`,
                borderRadius: `${RADIUS.md}px`,
                px: 1.5,
                py: 1,
              }}
            >
              <Stack direction="row" alignItems="center" gap={1}>
                <Typography sx={PORTAL_TEXT.cellSecondary}>
                  {selectedRule.target_text}
                </Typography>
                <ArrowForwardIcon
                  sx={{ fontSize: 14, color: 'text.disabled' }}
                />
                <Typography sx={PORTAL_TEXT.cellSecondary}>
                  {selectedRule.replacement_text}
                </Typography>
              </Stack>
              {selectedRule.description && (
                <Typography sx={{ ...PORTAL_TEXT.cellMeta, mt: 0.5 }}>
                  説明: {selectedRule.description}
                </Typography>
              )}
            </Box>
          )}
          <Typography sx={PORTAL_TEXT.cellMeta}>
            ※ この操作は取り消せません
          </Typography>
        </Stack>
      </DialogShell>
    </PageScaffold>
  )
}
